import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { storyblokEditable } from "@storyblok/react";
import { useNavigate } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import {
  ListPanelModel,
  QuoteModel,
  SolutionsModel,
  VideoModel,
} from "./ListPanelModel";
import { ProductModel } from "../ShowcasePanel/ShowcasePanelModel";
import { PageModel } from "../../core/Models/PageModel";
import ListPanelUtils from "./ListPanelUtils";
import { PeopleItemModel } from "./PeopleItem/PeopleItemModel";
import PeopleItem from "./PeopleItem/PeopleItem";
import BenefitItem from "./BenefitItem/BenefitItem";
import ListPanelItem from "./ListPanelItem/ListPanelItem";
import { Theme } from "../../core/Models/CustomSiteConfigModel";
import { themeService } from "../../core/NotificationStripNav/ThemeService";

import "./ListPanel.css";

const ListPanel: React.FC<{ blok: ListPanelModel }> = ({ blok }) => {
  const [theme, setTheme] = useState<Theme | null>(themeService.getTheme());

  const navigate = useNavigate();
  const contentRef = useRef<HTMLDivElement | null>(null);
  const listPanelRef = useRef<HTMLDivElement | null>(null);
  const [showPopup, setShowPopup] = useState<string | undefined>(undefined);

  const handleClick = (
    entity:
      | QuoteModel
      | ProductModel
      | VideoModel
      | PageModel
      | PeopleItemModel
      | SolutionsModel,
    uuid: string
  ) => {
    if (ListPanelUtils.isVideoEntity(entity)) {
      setShowPopup(uuid);
    }
  };

  const handleHyperlinkClick = () => {
    if (blok.link && blok.link.length > 0 && blok.link[0].link.cached_url) {
      if (blok.link[0].link.linktype === "story") {
        navigate(`/${blok.link[0].link?.cached_url}`);
      } else {
        const url = new URL(
          blok.link[0].link?.cached_url,
          window.location.origin
        );
        window.location.href = url.href;
      }
    }
  };

  useLayoutEffect(() => {
    if (blok.itemsClass === "horizontal-scroll" && window.innerWidth > 768) {
      gsap.registerPlugin(ScrollTrigger);
      const panel = contentRef.current;
      const wrapper = listPanelRef.current;

      if (!panel || !wrapper) return;

      const scrollWidth = panel.scrollWidth;
      const navHeight =
        document.querySelector(".notification-strip-nav")?.clientHeight ?? 0;

      const animation = gsap.to(panel, {
        x: () => `-${panel.scrollWidth - panel.clientWidth}px`,
        ease: "none",
        scrollTrigger: {
          trigger: wrapper,
          start: `-${navHeight} top`,
          end: () => `+=${scrollWidth}`,
          scrub: true,
          pin: true,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        },
      });

      return () => {
        if (panel) {
          gsap.killTweensOf(panel);
        }
        animation.kill();
        ScrollTrigger.getAll().forEach((trigger) => trigger?.kill());
      };
    }
  }, [blok.itemsClass]);

  useEffect(() => {
    const unsubscribe = themeService.subscribe(setTheme);
    return () => unsubscribe();
  }, []);

  return (
    <div
      {...storyblokEditable(blok)}
      className="list-panel"
      style={{ backgroundColor: blok.panelColour }}
    >
      <div className="list-panel-ref" ref={listPanelRef}>
        <div className="list-panel-container" style={{ color: blok.textColor }}>
          <div className="title">
            <span>{blok.title}</span>
          </div>
          {blok.link && blok.link.length > 0 && (
            <div>
              <span
                className="list-panel-hyperlink"
                onClick={handleHyperlinkClick}
                style={{
                  color: blok.link[0].textColor || theme?.hyperlinksColor,
                }}
              >
                {blok.link[0].label}
              </span>
            </div>
          )}
          <div
            className={`list-panel-content ${blok.itemsClass}`}
            ref={contentRef}
          >
            {blok.entities
              ? blok.entities.map((entity) => {
                  if (!entity || !entity.content) {
                    return null;
                  }
                  if (ListPanelUtils.isBenefitEntity(blok, entity.content)) {
                    return blok.benefits.map((benefitId) => (
                      <BenefitItem
                        blok={entity.content as ProductModel}
                        benefitId={benefitId}
                        containerColor={blok.containerColor}
                        fontFamily={blok.style}
                        key={`benefit-item-${blok._uid}-${benefitId}`}
                      ></BenefitItem>
                    ));
                  } else if (ListPanelUtils.isPeopleEntity(entity.content)) {
                    return (
                      <PeopleItem
                        key={`list-panell-people-item-${entity.content?._uid}`}
                        blok={entity.content as PeopleItemModel}
                      ></PeopleItem>
                    );
                  } else {
                    return (
                      <ListPanelItem
                        key={`list-panel-ite-${
                          typeof entity.uuid === "string"
                            ? entity.uuid
                            : (entity.uuid as any)?.uuid
                        }`}
                        entity={entity}
                        containerColor={blok.containerColor}
                        fontFamily={blok.style}
                        showPopup={showPopup}
                        handleClick={() =>
                          handleClick(entity.content, entity.uuid)
                        }
                        onVideoButtonClick={() => setShowPopup(undefined)}
                      ></ListPanelItem>
                    );
                  }
                })
              : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListPanel;
